import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import type { MetricPeriod } from '@manaro/contracts'
import { useOverview, useRefreshOverview } from '@/features/dashboard/use-overview.ts'
import { AttentionCard } from '@/features/dashboard/components/attention-card.tsx'
import { KpiCard } from '@/features/dashboard/components/kpi-card.tsx'
import { LoadCard } from '@/features/dashboard/components/load-card.tsx'
import { PeriodSwitch } from '@/features/dashboard/components/period-switch.tsx'
import { PlansCard } from '@/features/dashboard/components/plans-card.tsx'
import { SalonsChart } from '@/features/dashboard/components/salons-chart.tsx'
import { ApiError } from '@/shared/api/client.ts'
import { Card } from '@/shared/ui/card.tsx'
import { ErrorState, Skeleton } from '@/shared/ui/states.tsx'
import { formatTime } from '@/shared/lib/format.ts'

const PRIMARY_KPIS = ['mrr', 'paying', 'new_accounts', 'paid_share']

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Card key={index} className="flex min-w-0 flex-col gap-2 px-5 py-4.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-3 w-20" />
          </Card>
        ))}
      </div>
      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-[220px] rounded-card" />
        <Skeleton className="h-[220px] rounded-card" />
      </div>
    </div>
  )
}

export function DashboardPage() {
  const [period, setPeriod] = useState<MetricPeriod>('7 дней')
  const { data, isPending, error, refetch } = useOverview(period)
  const { mutate: refresh, isPending: isRefreshing } = useRefreshOverview(period)

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[300px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Ключевые показатели
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Деньги, подписки и активность салонов — то, по чему видно, растёт ли сервис.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodSwitch value={period} onChange={setPeriod} />
          <button
            type="button"
            onClick={() => refresh()}
            disabled={isRefreshing || isPending}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-surface-soft px-3 py-1.5 text-[13px] whitespace-nowrap text-ink-muted transition-colors hover:text-ink disabled:opacity-60"
          >
            <RefreshCw className={isRefreshing ? 'size-3.5 animate-spin' : 'size-3.5'} />
            {isRefreshing
              ? 'Считаем…'
              : data?.generatedAt
                ? `Посчитано в ${formatTime(data.generatedAt)}`
                : 'Обновить'}
          </button>
        </div>
      </header>

      {isPending && <DashboardSkeleton />}

      {error && (
        <Card>
          <ErrorState
            message={error instanceof ApiError ? error.message : 'Не удалось загрузить показатели.'}
            onRetry={() => void refetch()}
          />
        </Card>
      )}

      {data && (
        <div className="flex flex-col gap-3.5">
          <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
            {data.kpis
              .filter((kpi) => PRIMARY_KPIS.includes(kpi.key))
              .map((kpi) => (
                <KpiCard key={kpi.key} kpi={kpi} currency={data.currency} />
              ))}
          </div>

          <div className="grid gap-3.5 sm:grid-cols-3">
            {data.kpis
              .filter((kpi) => !PRIMARY_KPIS.includes(kpi.key))
              .map((kpi) => (
                <KpiCard key={kpi.key} kpi={kpi} currency={data.currency} variant="compact" />
              ))}
          </div>

          <div className="grid gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <SalonsChart chart={data.chart} />
            <PlansCard plans={data.plans} currency={data.currency} />
          </div>

          <div className="grid gap-3.5 lg:grid-cols-2">
            <LoadCard load={data.load} />
            <AttentionCard items={data.attention} />
          </div>

          {(data.origin === 'demo' || data.notes.length > 0) && (
            <div className="flex flex-col gap-1.5 text-[12.5px] text-ink-muted text-balance-pretty">
              {data.origin === 'demo' && (
                <p className="font-medium text-warn-ink">Данные витринные — бэкенд Manaro сейчас недоступен.</p>
              )}
              {data.notes.map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
